// Classes tab (F-STN-21; spec/13 §12): a venue's price and permission tiers — Standard, Pro,
// Premium, Bays — as cards to scan. "New class" and Edit open a centred dialog: a short form above a
// live preview of the floor tile a station of the class will wear, not a long scroll.
//
// A class carries a name, a colour token (never a hex), a pricing mode (per player or per station),
// a default capacity, an optional age limit and a description shown to players (spec/04 §3). Its
// station count is derived here from the venue's stations. Creating or editing needs `stations:manage`;
// a member without it sees the cards read-only.
//
// Where the venue has Clavian Boot (F-BOOT-27, ADR-0065 decision 5), a class also decides what its PCs start: each card
// names its Windows image, and the Edit dialog has a Windows & games tab (`../boot/ClassBoot.tsx`), changed with
// `boot:manage` and `stations:manage`. A Boot-only venue uses classes the same way.

import type { ColorToken, CreateStationClass, PricingMode, Station, StationClass, UpdateStationClass } from '@clavian/api-client';
import { MAX_CLASS_AGE_LIMIT, MAX_STATION_CAPACITY, PRICING_MODES } from '@clavian/contracts/constants';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, EmptyState, SegmentedControl, Tabs, TextField, useToast } from '@clavian/ui-web';
import { useMemo, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';

import { ClassBoot, classWindowsName } from '../boot/ClassBoot';
import { useBootClasses, useBootImages } from '../boot/queries';

import { PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { LoadFailure } from '../shell/LoadFailure';
import { Icon } from '../shell/nav-icons';
import { CardsSkeleton, Facts, Notice, Toolbar, ToolbarSpring } from '../ui';
import { ClassSettings } from './ClassSettings';
import { ClassTilePreview, ColorTokenField, tokenColor } from './fields';
import { useCreateStationClass, useStationClasses, useStations, useUpdateStationClass } from './queries';

/** What a class's PCs boot, where the venue has Clavian Boot: shown with `boot:view`, changed with `boot:manage` too. */
export interface ClassBootRights {
  readonly view: boolean;
  readonly edit: boolean;
}

const NO_BOOT: ClassBootRights = { view: false, edit: false };

export function ClassesTab({
  organizationId,
  venueId,
  canManage,
  boot = NO_BOOT,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly canManage: boolean;
  readonly boot?: ClassBootRights;
}): ReactNode {
  const { t } = useTranslation('console');
  const classes = useStationClasses(organizationId, venueId);
  const stations = useStations(organizationId, venueId);
  const [editing, setEditing] = useState<StationClass | 'new' | null>(null);
  const bootClasses = useBootClasses({ organizationId, venueId }, boot.view);
  const bootImages = useBootImages({ organizationId, venueId }, boot.view);
  const windowsOf = (classId: string): string | null =>
    boot.view ? classWindowsName(bootClasses.data?.find((entry) => entry.classId === classId) ?? (bootClasses.data === undefined ? undefined : { classId, imageId: null, gameDiskIds: null }), bootImages.data, t) : null;

  const countByClass = useMemo(() => {
    const counts = new Map<string, number>();
    for (const station of (stations.data?.pages ?? []).flatMap((page) => page.data) as Station[]) {
      counts.set(station.classId, (counts.get(station.classId) ?? 0) + 1);
    }
    return counts;
  }, [stations.data]);

  let body: ReactNode;
  if (classes.data === undefined) {
    body = classes.isError ? (
      <LoadFailure error={classes.error} message={t('stations.classes.unavailable')} onRetry={() => void classes.refetch()} />
    ) : (
      <CardsSkeleton count={3} height="11rem" />
    );
  } else if (classes.data.length === 0) {
    body = (
      <EmptyState
        icon={<Icon name="layers" />}
        title={t('stations.classes.emptyTitle')}
        description={t('stations.classes.empty')}
        action={
          canManage ? (
            <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setEditing('new')}>
              {t('stations.classes.new')}
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    body = (
      <ul className="console-class-cards" aria-label={t('stations.classes.listLabel')}>
        {classes.data.map((klass) => (
          <li key={klass.id}>
            <ClassCard klass={klass} stationCount={countByClass.get(klass.id) ?? 0} windows={windowsOf(klass.id)} canManage={canManage || boot.edit} onEdit={() => setEditing(klass)} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="console-tabpane">
      <Toolbar>
        <p className="console-muted console-tabpane__intro">{t('stations.classes.intro')}</p>
        <ToolbarSpring />
        {canManage && classes.data !== undefined && classes.data.length > 0 ? (
          <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setEditing('new')}>
            {t('stations.classes.new')}
          </Button>
        ) : null}
      </Toolbar>
      {body}
      <ClassDialog
        organizationId={organizationId}
        venueId={venueId}
        editing={editing === 'new' || editing === null ? undefined : editing}
        canManage={canManage}
        boot={boot}
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
    </div>
  );
}

function pricingLabelOf(t: ReturnType<typeof useTranslation>['t'], mode: PricingMode): string {
  return t(`stations.pricingMode.${mode}`);
}

function ClassCard({
  klass,
  stationCount,
  windows,
  canManage,
  onEdit,
}: {
  readonly klass: StationClass;
  readonly stationCount: number;
  /** What its PCs start (Clavian Boot), in words; null without it. */
  readonly windows: string | null;
  readonly canManage: boolean;
  readonly onEdit: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    // The class's colour as the rail along its card's top — the swatch the floor's group heading shows (ADR-0043).
    <article
      className="console-class-card console-rail"
      data-rail="class"
      style={{ '--tile-class': tokenColor(klass.colorToken), '--console-rail-class': tokenColor(klass.colorToken) } as CSSProperties}
    >
      <header className="console-class-card__head">
        <span className="console-class-card__swatch" aria-hidden="true" />
        <h3 className="console-class-card__title">{klass.name}</h3>
        {canManage ? (
          <Button variant="ghost" size="sm" onClick={onEdit} label={t('stations.classes.editNamed', { name: klass.name })}>
            <span>{t('stations.classes.edit')}</span>
          </Button>
        ) : null}
      </header>
      {klass.description === '' ? null : <p className="console-class-card__desc">{klass.description}</p>}
      <Facts
        columns={2}
        items={[
          { key: 'stations', label: t('stations.classes.field.stations'), value: <span className="console-figure console-class-card__count">{stationCount}</span> },
          { key: 'pricing', label: t('stations.classes.field.pricing'), value: pricingLabelOf(t, klass.pricingMode) },
          { key: 'capacity', label: t('stations.classes.field.capacity'), value: t('stations.classes.seats', { count: klass.defaultCapacity }) },
          { key: 'age', label: t('stations.classes.field.age'), value: klass.ageLimit === null ? t('stations.classes.noAge') : t('stations.classes.agePreview', { age: klass.ageLimit }) },
          ...(windows === null ? [] : [{ key: 'windows', label: t('boot.classes.cardWindows'), value: windows }]),
        ]}
      />
    </article>
  );
}

interface ClassFormState {
  readonly name: string;
  readonly colorToken: ColorToken;
  readonly pricingMode: PricingMode;
  readonly capacity: string;
  readonly ageLimit: string;
  readonly description: string;
}

function formFor(editing: StationClass | undefined): ClassFormState {
  if (editing === undefined) {
    // A new class starts on the first categorical hue (#411/ADR-0025); the picker offers only these.
    return { name: '', colorToken: 'category-1', pricingMode: 'per_player', capacity: '1', ageLimit: '', description: '' };
  }
  return {
    name: editing.name,
    colorToken: editing.colorToken,
    pricingMode: editing.pricingMode,
    capacity: String(editing.defaultCapacity),
    ageLimit: editing.ageLimit === null ? '' : String(editing.ageLimit),
    description: editing.description,
  };
}

/**
 * The New/Edit class dialog (spec/13 §12): the live tile preview on top, the short form under it.
 * `editing` undefined creates; a class edits it.
 */
function ClassDialog({
  organizationId,
  venueId,
  editing,
  canManage,
  boot,
  open,
  onOpenChange,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly editing?: StationClass;
  readonly canManage: boolean;
  readonly boot: ClassBootRights;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      dirty="auto"
      eyebrow={t('stations.tabs.classes')}
      title={editing === undefined ? t('stations.classes.new') : t('stations.classes.editTitle')}
      description={t('stations.classes.drawerHint')}
    >
      {open ? (
        editing === undefined ? (
          <ClassForm key="new" organizationId={organizationId} venueId={venueId} editing={undefined} onDone={() => onOpenChange(false)} onCreated={undefined} />
        ) : (
          // The class, and its own PC settings (spec/13 §12): two tabs of one dialog, each with its own Save, so neither
          // form's pinned actions sit over the other's rows. A new class gets its PC settings once it exists.
          <Tabs
            className="console-class-tabs"
            label={t('stations.classes.editTitle')}
            items={[
              {
                value: 'class',
                label: t('stations.classes.tabClass'),
                content: <ClassForm key={editing.id} organizationId={organizationId} venueId={venueId} editing={editing} onDone={() => onOpenChange(false)} onCreated={undefined} />,
              },
              {
                value: 'pc',
                label: t('settings.classSettings.title'),
                content: <ClassSettings organizationId={organizationId} venueId={venueId} classId={editing.id} />,
              },
              // What its PCs start from the boot server (Clavian Boot): its Windows image and games.
              ...(boot.view
                ? [{ value: 'boot', label: t('boot.classes.tab'), content: <ClassBoot scope={{ organizationId, venueId }} classId={editing.id} canEdit={boot.edit && canManage} /> }]
                : []),
            ]}
          />
        )
      ) : null}
    </Dialog>
  );
}

/**
 * The class form with its live tile preview. Exported so adding a station can create a class in place, as a step
 * of its own dialog, when no class fits (`AddStationDialog`) — never a second dialog on top.
 */
export function ClassForm({
  organizationId,
  venueId,
  editing,
  onDone,
  onCreated,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly editing: StationClass | undefined;
  readonly onDone: () => void;
  readonly onCreated: ((created: StationClass) => void) | undefined;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const create = useCreateStationClass(organizationId, venueId);
  const update = useUpdateStationClass(organizationId, venueId);
  const [form, setForm] = useState<ClassFormState>(() => formFor(editing));
  const [errors, setErrors] = useState<{ name?: string; capacity?: string; ageLimit?: string; form?: string }>({});

  const capacityNumber = Number(form.capacity);
  const ageNumber = form.ageLimit.trim() === '' ? null : Number(form.ageLimit);
  const busy = create.isPending || update.isPending;

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: typeof errors = {};
    if (form.name.trim() === '') next.name = t('stations.classes.error.nameRequired');
    if (!Number.isInteger(capacityNumber) || capacityNumber < 1 || capacityNumber > MAX_STATION_CAPACITY) {
      next.capacity = t('stations.classes.error.capacityRange', { max: MAX_STATION_CAPACITY });
    }
    if (ageNumber !== null && (!Number.isInteger(ageNumber) || ageNumber < 1 || ageNumber > MAX_CLASS_AGE_LIMIT)) {
      next.ageLimit = t('stations.classes.error.ageRange', { max: MAX_CLASS_AGE_LIMIT });
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const onError = (error: unknown): void => {
      const nameMessage = problem.fieldMessage(error, 'name');
      if (problemCode(error) === PROBLEM.conflict) setErrors({ name: t('stations.classes.error.nameTaken') });
      else if (nameMessage !== undefined) setErrors({ name: nameMessage });
      else setErrors({ form: problem.text(error, { fallback: t('stations.classes.error.refused') }) });
    };

    if (editing === undefined) {
      const body: CreateStationClass = {
        name: form.name.trim(),
        colorToken: form.colorToken,
        pricingMode: form.pricingMode,
        defaultCapacity: capacityNumber,
        ...(ageNumber === null ? {} : { ageLimit: ageNumber }),
        ...(form.description.trim() === '' ? {} : { description: form.description.trim() }),
      };
      create.mutate(body, {
        onSuccess: (created) => {
          show({ tone: 'ok', title: t('stations.classes.created', { name: created.name }) });
          onCreated?.(created);
          onDone();
        },
        onError,
      });
    } else {
      const body: UpdateStationClass = {
        name: form.name.trim(),
        colorToken: form.colorToken,
        pricingMode: form.pricingMode,
        defaultCapacity: capacityNumber,
        ageLimit: ageNumber,
        description: form.description.trim(),
      };
      update.mutate(
        { classId: editing.id, body },
        {
          onSuccess: () => {
            show({ tone: 'ok', title: t('stations.classes.saved', { name: form.name.trim() }) });
            onDone();
          },
          onError,
        },
      );
    }
  }

  return (
    <div className="console-dialog-stack">
      <section className="console-dialog-preview" aria-label={t('stations.classes.previewLabel')}>
        <span className="console-eyebrow">{t('stations.classes.previewLabel')}</span>
        <ClassTilePreview
          name={form.name.trim()}
          colorToken={form.colorToken}
          pricingLabel={pricingLabelOf(t, form.pricingMode)}
          capacity={Number.isInteger(capacityNumber) && capacityNumber > 0 ? capacityNumber : 1}
          ageLimit={ageNumber !== null && Number.isInteger(ageNumber) ? ageNumber : null}
        />
      </section>
      <form className="console-form" aria-label={t('stations.classes.formLabel')} noValidate onSubmit={submit}>
        {errors.form !== undefined ? (
          <Notice tone="danger" role="alert">
            {errors.form}
          </Notice>
        ) : null}
        <TextField label={t('stations.classes.field.name')} required value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} error={errors.name} />
        <ColorTokenField label={t('stations.classes.field.color')} value={form.colorToken} onValueChange={(colorToken) => setForm((current) => ({ ...current, colorToken }))} />
        <div className="console-field-block">
          <SegmentedControl<PricingMode>
            label={t('stations.classes.field.pricing')}
            value={form.pricingMode}
            onValueChange={(value) => setForm((current) => ({ ...current, pricingMode: value }))}
            options={PRICING_MODES.map((mode) => ({ value: mode, label: t(`stations.pricingMode.${mode}`) }))}
          />
          <p className="console-subtle">{t(`stations.pricingMode.${form.pricingMode}Hint`)}</p>
        </div>
        <div className="console-form__row">
          <TextField
            label={t('stations.classes.field.capacity')}
            description={t('stations.classes.capacityHint', { max: MAX_STATION_CAPACITY })}
            required
            numeric
            inputMode="numeric"
            value={form.capacity}
            onChange={(event) => setForm((current) => ({ ...current, capacity: event.target.value }))}
            error={errors.capacity}
          />
          <TextField
            label={t('stations.classes.field.age')}
            description={t('stations.classes.ageHint')}
            showOptional
            numeric
            inputMode="numeric"
            value={form.ageLimit}
            onChange={(event) => setForm((current) => ({ ...current, ageLimit: event.target.value }))}
            error={errors.ageLimit}
          />
        </div>
        <TextField
          label={t('stations.classes.field.description')}
          description={t('stations.classes.descriptionHint')}
          showOptional
          value={form.description}
          onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
        />
        <div className="console-form__actions console-form__actions--end">
          <Button type="button" variant="ghost" onClick={onDone}>
            {t('stations.classes.cancel')}
          </Button>
          <Button type="submit" variant="primary" busy={busy}>
            {editing === undefined ? t('stations.classes.create') : t('stations.classes.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
